import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { School, FileText, ArrowRight, LogIn } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    udise_code: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(formData.udise_code, formData.password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      const data = error.response?.data;
      const message = data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex flex-col justify-center items-center bg-navy relative overflow-hidden p-12">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/5548399/pexels-photo-5548399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 text-center">
          <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-xl">
            <School className="w-14 h-14 text-navy" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-white mb-4">
            Welcome Back
          </h1>
          <p className="text-slate-300 text-lg mb-8">
            Login to Teacher Utility Toolkit
          </p>
          <div className="flex items-center justify-center gap-4 text-white/80">
            <FileText className="w-5 h-5" />
            <span>Generate reports in minutes</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading font-bold text-navy text-xl">Teacher Utility Toolkit</span>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="font-heading text-2xl text-navy">Login to Your Account</CardTitle>
              <CardDescription>
                Enter your UDISE code and password to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="udise_code">UDISE Code</Label>
                  <Input
                    id="udise_code"
                    name="udise_code"
                    placeholder="Enter your UDISE code"
                    value={formData.udise_code}
                    onChange={handleChange}
                    required
                    className="uppercase"
                    data-testid="login-udise"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    data-testid="login-password"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-navy hover:bg-navy-dark text-white py-6 text-lg font-semibold"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Logging in...' : 'Login'}
                  <LogIn className="w-5 h-5 ml-2" />
                </Button>

                <p className="text-center text-sm text-slate-500">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-amber hover:underline font-medium">
                    Register here
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-slate-500 hover:text-navy transition">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
